// Searchable font picker: category chips + search over the ~140-family
// catalog (bundled Teacher picks pinned first). Rows preview in their own
// face — Google families lazy-load on hover so we never bulk-download the
// catalog. Selecting ensures the font is registered before applying.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FONT_CATALOG, FONT_CATEGORIES } from '../fonts/fontCatalog';
import { ensureFont, isFontRegistered } from '../fonts/fontLoader';

const RECENT_KEY = 'cce_recent_fonts';
function getRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch (_) { return []; }
}
function pushRecent(family) {
  try {
    const next = [family, ...getRecent().filter((f) => f !== family)].slice(0, 8);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch (_) { /* ignore */ }
}

export default function FontPicker({ value, onSelect }) {
  const { t } = useTranslation('editor');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [loadingFamily, setLoadingFamily] = useState(null);
  const [, bumpLoaded] = useState(0); // re-render as hovered fonts finish loading
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FONT_CATALOG.filter(
      (f) => (category === 'All' || f.category === category)
        && (!q || f.family.toLowerCase().includes(q)),
    );
  }, [query, category]);

  const recent = useMemo(
    () => getRecent().filter((f) => FONT_CATALOG.some((c) => c.family === f)),
    [open], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const choose = async (family) => {
    setLoadingFamily(family);
    const res = await ensureFont(family);
    setLoadingFamily(null);
    if (res && res.ok === false) {
      window.dispatchEvent(new CustomEvent('cce-font-load-failed', { detail: { family } }));
      return;
    }
    pushRecent(family);
    onSelect(family);
    setOpen(false);
  };

  const hoverLoad = (f) => {
    if (f.source === 'google' && !isFontRegistered(f.family)) {
      ensureFont(f.family).then(() => bumpLoaded((n) => n + 1));
    }
  };

  const renderRow = (f) => (
    <button
      key={f.family}
      type="button"
      className={`cce-fp-row${value === f.family ? ' active' : ''}`}
      onMouseEnter={() => hoverLoad(f)}
      onClick={() => choose(f.family)}
    >
      <span className="cce-fp-name" style={{ fontFamily: `'${f.family}', sans-serif` }}>
        {f.family}
      </span>
      {loadingFamily === f.family && <span className="cce-fp-hint">{t('fontPicker.loading')}</span>}
      {f.source === 'bundled' && <span className="cce-fp-hint" title={t('fontPicker.bundledTitle')}>★</span>}
    </button>
  );

  return (
    <div className="cce-fp-anchor" ref={ref}>
      <button
        type="button"
        className="cce-fp-trigger"
        title={t('fontPicker.fontTitle')}
        aria-label={t('fontPicker.fontFamily')}
        onClick={() => setOpen((o) => !o)}
      >
        <span style={{ fontFamily: `'${value}', sans-serif` }}>{value}</span>
        <span className="cce-fp-caret">▾</span>
      </button>

      {open && (
        <div className="cce-fp-panel" role="listbox" aria-label={t('fontPicker.fontList')}>
          <input
            type="text"
            className="cce-fp-search"
            placeholder={t('fontPicker.searchPlaceholder')}
            value={query}
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="cce-fp-cats">
            {FONT_CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                className={`cce-fp-cat${category === c ? ' active' : ''}`}
                onClick={() => setCategory(c)}
              >
                {t(`fontPicker.categories.${c.toLowerCase()}`, { defaultValue: c })}
              </button>
            ))}
          </div>
          <div className="cce-fp-list">
            {recent.length > 0 && !query && category === 'All' && (
              <>
                <div className="cce-fp-section">{t('fontPicker.recentlyUsed')}</div>
                {recent.map((family) => {
                  const f = FONT_CATALOG.find((c) => c.family === family);
                  return f ? renderRow(f) : null;
                })}
                <div className="cce-fp-section">{t('fontPicker.allFonts')}</div>
              </>
            )}
            {list.map(renderRow)}
            {list.length === 0 && <div className="cce-fp-empty">{t('fontPicker.noMatch', { query })}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
